import { Fragment, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import MarcoReporte, {
  TD,
  TH,
  TarjetaTotal,
} from '../../components/reportes/MarcoReporte';
import {
  Casilla,
  SelectCategoria,
} from '../../components/reportes/FiltrosComunes';
import { useConteo } from '../../hooks/useReportes';
import BloqueLectura from '../../components/reportes/BloqueLectura';

/**
 * Reporte 5 — Planilla de conteo físico (PROMPT_N°054).
 *
 * El valor de esta pantalla es poder imprimirla: las columnas Conteo y
 * Observación van vacías a propósito.
 *
 * "Mostrar stock del sistema" viene apagado. Si el que cuenta ve primero
 * lo que el sistema cree tener, confirma en vez de contar. Encenderlo
 * sirve para un recuento dirigido sobre diferencias ya detectadas, que es
 * otro ejercicio y conviene que sea una decisión explícita.
 */

export default function ReporteConteo() {
  const [params, setParams] = useSearchParams();

  const poner = (clave: string, valor: string | boolean) => {
    const p = new URLSearchParams(params);
    const v = String(valor);
    if (v === '' || v === 'undefined') p.delete(clave);
    else p.set(clave, v);
    setParams(p, { replace: true });
  };

  /**
   * Aplica el filtro de una frase clickeable y baja a la tabla. Los filtros
   * viven en la query string (N°053), así que basta con escribirlos ahí:
   * el reporte se vuelve a consultar solo y el link queda compartible.
   */
  const aplicarFiltroDeFrase = (
    filtro: Record<string, string | number | boolean>
  ) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(filtro)) p.set(k, String(v));
    setParams(p, { replace: true });
    document
      .getElementById('tabla-reporte')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filtros = useMemo(
    () => ({
      categoria_id: params.get('categoria_id') ?? undefined,
      en_bodega: params.get('en_bodega') !== 'false',
      mostrar_sistema: params.get('mostrar_sistema') === 'true',
    }),
    [params]
  );

  const { data, isLoading, error } = useConteo(filtros);
  const grupos = data?.grupos ?? [];

  return (
    <MarcoReporte
      nombre="conteo"
      titulo="Planilla de conteo físico"
      descripcion="Para imprimir, contar a mano y volver con los números escritos."
      filtros={filtros}
      cargando={isLoading}
      error={error}
      vacio={!isLoading && grupos.length === 0}
      lectura={
        <BloqueLectura
          lectura={data?.lectura}
          ayuda="conteo"
          cargando={isLoading}
          onFiltro={aplicarFiltroDeFrase}
        />
      }
      controles={
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SelectCategoria
            valor={params.get('categoria_id') ?? ''}
            onChange={(v) => poner('categoria_id', v)}
          />
          <div className="md:pt-5">
            <Casilla
              etiqueta="Sólo lo que estuvo en bodega"
              marcada={filtros.en_bodega}
              onChange={(v) => poner('en_bodega', v)}
            />
          </div>
          <div className="md:pt-5">
            <Casilla
              etiqueta="Mostrar stock del sistema"
              marcada={filtros.mostrar_sistema}
              onChange={(v) => poner('mostrar_sistema', v)}
              ayuda="Apagado se cuenta a ciegas, que es como se hace bien"
            />
          </div>
        </div>
      }
      aviso={
        filtros.mostrar_sistema ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-sm text-amber-900 dark:text-amber-200">
            La planilla va a mostrar el stock del sistema. Sirve para un
            recuento dirigido, pero quien cuenta tiende a confirmar el número
            que ve en vez de contar.
          </div>
        ) : null
      }
      totales={
        data ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TarjetaTotal
              etiqueta="Ingredientes a contar"
              valor={String(data.total)}
            />
            <TarjetaTotal
              etiqueta="Categorías"
              valor={String(grupos.length)}
              detalle="La impresión salta de página en cada una"
            />
          </div>
        ) : null
      }
    >
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900/40">
          <tr>
            <th className={TH}>Ingrediente</th>
            <th className={TH}>Marca</th>
            <th className={TH}>Unidad</th>
            {filtros.mostrar_sistema && (
              <th className={`${TH} text-right`}>Stock sistema</th>
            )}
            <th className={TH}>Conteo</th>
            {filtros.mostrar_sistema && <th className={TH}>Diferencia</th>}
            <th className={TH}>Observación</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {grupos.map((g) => (
            <Fragment key={g.categoria}>
              <tr className="bg-gray-100 dark:bg-gray-900/60">
                <td
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                  colSpan={filtros.mostrar_sistema ? 7 : 5}
                >
                  {g.categoria} · {g.filas.length}
                </td>
              </tr>
              {g.filas.map((f) => (
                <tr key={f.id}>
                  <td className={`${TD} font-medium`}>{f.nombre}</td>
                  <td className={TD}>{f.marca ?? '—'}</td>
                  <td className={TD}>{f.unidad}</td>
                  {filtros.mostrar_sistema && (
                    <td className={`${TD} text-right tabular-nums`}>
                      {(f.stock_sistema ?? 0).toLocaleString('es-CL')}
                    </td>
                  )}
                  <td className={`${TD} w-28`}>
                    <div className="h-6 border-b border-dashed border-gray-300 dark:border-gray-600" />
                  </td>
                  {filtros.mostrar_sistema && (
                    <td className={`${TD} w-28`}>
                      <div className="h-6 border-b border-dashed border-gray-300 dark:border-gray-600" />
                    </td>
                  )}
                  <td className={TD}>
                    <div className="h-6 border-b border-dashed border-gray-300 dark:border-gray-600" />
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </MarcoReporte>
  );
}
